<template>
  <main class="panzerglass">
    <Breadcrumb :items="breadcrumb" />

    <section class="panzerglass__section panzerglass__stage">
      <div class="panzerglass__stage-background" />
      <GridContainer class="panzerglass__stage-container">
        <Heading
          font-weight="ultra"
          size="lg"
          tag="h1"
          text-uppercase
          class="panzerglass__stage-header"
        >
          Wir sind
          <span class="panzerglass__stage-header-brand">Panzerglass®</span>
        </Heading>

        <Paragraph class="panzerglass__stage-copy">
          Die international anerkannte und preisgekrönte dänische Marke für Displayschutz. Wenn du
          ein Gerät hast, haben wir eine Lösung, die es schützt. Wir verkaufen Displayschutz und
          Hüllen für Smartphones, Tablets und Uhren.
        </Paragraph>

        <CdnImage
          src="/content/content-images/landingpages/panzerglass/6b05bd9734c976e1e2f0af355202ff24/panzerglass-header-image.png"
          alt="Eine Frau hält ein Handy mit einer Display-Schutzfolie in der Hand"
          class="panzerglass__stage-image"
          sizes="xs:100vw sm:50vw md:50vw lg:742px"
        />
      </GridContainer>
    </section>

    <section class="panzerglass__section panzerglass__features">
      <Heading
        font-weight="ultra"
        size="md"
        tag="h2"
        text-uppercase
        class="panzerglass__features-title"
      >
        Wir wagen es, anders zu sein und einen Mehrwert zu bieten.
      </Heading>
      <div class="panzerglass__features-container">
        <CdnImage
          alt="Smartphone mit PanzerGlass Schutz"
          src="/content/content-images/landingpages/panzerglass/7284895951806860c7a7af1b062085b8/16plus_PG_UWF_e-com.webp"
          class="panzerglass__features-img"
          render-without-figure
          sizes="xs:100vw sm:400px md:800px"
          lazy
          style="aspect-ratio: 955 / 1176"
        />
        <!-- arrows & text -->
        <div
          ref="features1"
          class="panzerglass__features-item panzerglass__features-item-top-left"
        >
          <CdnImage
            src="/content/content-images/landingpages/panzerglass/03010319f829246ebfac900e98cfd88f/icon-drop-protection@2x.png"
            render-without-figure
            lazy
            class="panzerglass__features-item-icon"
            style="aspect-ratio: 100 / 113"
            sizes="md:100px"
          />
          Drop protection
        </div>
        <div
          ref="features2"
          class="panzerglass__features-item panzerglass__features-item-top-right"
        >
          <CdnImage
            src="/content/content-images/landingpages/panzerglass/9fddf9e98060e4ba7b13dc2ca15a38dc/icon-easy-installation@2x.png"
            render-without-figure
            lazy
            style="aspect-ratio: 100 / 90"
            class="panzerglass__features-item-icon"
          />
          Easy installation
        </div>
        <div
          ref="features3"
          class="panzerglass__features-item panzerglass__features-item-bottom-left"
        >
          <CdnImage
            src="/content/content-images/landingpages/panzerglass/53d5538a7bf3654d4f236c568eb9721a/icon-scratch-resistant@2x.png"
            render-without-figure
            lazy
            style="aspect-ratio: 100 / 109"
            class="panzerglass__features-item-icon"
          />
          Scratch resistant
        </div>
        <div
          ref="features4"
          class="panzerglass__features-item panzerglass__features-item-bottom-right"
        >
          <CdnImage
            src="/content/content-images/landingpages/panzerglass/4c87c16bf0ab997544cd2cc9b3b685b0/icon-ultra-wide-fit@2x.png"
            render-without-figure
            lazy
            style="aspect-ratio: 100 / 103"
            class="panzerglass__features-item-icon"
          />
          Ultra-Wide fit
        </div>
        <!-- end of arrows & text -->
      </div>
    </section>

    <section class="panzerglass__section panzerglass__video">
      <GridContainer class="panzerglass__video-container">
        <VideoPlayer
          class="panzerglass__video-player"
          autoplay
          muted
          src="/content/content-videos/landingpages/panzerglass/274fb9035dcda768c6856cf1ed64f0ea/panzerglass-Widescreen-no-audio.webm"
          :overlay-opacity="0.9"
        />
      </GridContainer>
    </section>

    <section class="panzerglass__section panzerglass__image-text-rows">
      <Heading
        size="md"
        tag="h3"
        text-uppercase
        align="center"
        font-weight="ultra"
        spaces-bottom
      >
        Entdecke unsere Produktkategorien
      </Heading>

      <ImageTextGridRow
        v-for="item in textImageRows"
        :key="item.title"
        heading-tag="h4"
        heading-size="md"
        heading-text-uppercase
        :image-alignment="item.align"
        class="panzerglass__image-text-rows-entry"
      >
        <template #heading>
          {{ item.title }}
        </template>
        <template #copy>
          <ul class="list panzerglass__image-text-rows-list">
            <li
              v-for="(bulletItem, i) in item.contentList"
              :key="i"
              class="list__element list__element--bulletpoint"
              v-html="bulletItem"
            />
          </ul>
          <LinkButton
            primary
            tag="nuxt-link"
            :tealium-name="`zu-den-produkten-${slugify(item.linkText)}`"
            :to="item.href"
          >
            {{ item.linkText }}
          </LinkButton>
        </template>
        <template #image>
          <div>
            <CdnImage
              :style="item.style"
              :alt="item.alt"
              :src="item.src"
              render-without-figure
              lazy
            />
          </div>
        </template>
      </ImageTextGridRow>
    </section>

    <section class="panzerglass__section panzerglass__offer-tile-xl-container">
      <ProductOfferTileXlContainer
        v-for="(teaser, index) in getOffersByGroup(promotionsWithOffers, 'highlight')"
        :key="teaser.type"
        v-bind="teaser"
        class="panzerglass__offer-tile-xl"
        :class="teaser.type"
        :alignment="index % 2 === 0 ? 'left' : 'right'"
        show-brand-logo
        lazy
      >
      </ProductOfferTileXlContainer>
    </section>

    <section class="panzerglass__section panzerglass__sustainability">
      <CdnImage
        src="/content/content-images/landingpages/panzerglass/59401f4985d971872350236e4b66a856/artikel-content-s@2x.jpg"
        src-md="/content/content-images/landingpages/panzerglass/89b75a4e48df37174f7558ec4eed1617/artikel-content-l@2x.jpg"
        alt=""
        lazy
        class="panzerglass__sustainability-bg"
      />
      <div class="panzerglass__sustainability-container">
        <CdnImage
          src="/content/content-images/landingpages/panzerglass/f453450d6b5fb0d6e458b8b654adc61a/claim-sustainability@2x.png"
          alt="Taking Action that matters"
          lazy
          render-without-figure
          class="panzerglass__sustainability-claim"
        />
        <div class="panzerglass__sustainability-content">
          <Heading
            font-weight="ultra"
            size="md"
            tag="h2"
            text-uppercase
            spaces-bottom
          >
            Sustainability:<br />Faktenbasierte Nachhaltigkeit
          </Heading>
          <div>
            Im Jahr 2024 werden wir unsere CO2 Emissionen durch die ISO 14064-Zertifizierung
            erstellen. Wir dokumentieren die Kohlenstoff Emissionen in unserem eigenen Betrieb
            (Scope 1 & 2) und in unserer Wertschöpfungskette (Scope 3), in Anlehnung an das
            Treibhausgasprotokoll. Für unsere Produkte verwenden wir einen Lebenszyklusansatz (LCA)
            und messen den spezifischen CO2-Fußabdruck für jedes Produkt.
          </div>
        </div>
      </div>
    </section>

    <GridContainer class="panzerglass__section panzerglass__product-finder">
      <Heading
        font-weight="ultra"
        size="md"
        tag="h2"
        text-uppercase
        align="center"
        class="panzerglass__product-finder-title"
      >
        Panzerglass® QUICK FINDER
      </Heading>

      <div class="panzerglass__product-finder-filter">
        <LinkButton
          v-for="brand in brands"
          :key="brand.slug"
          class="panzerglass__product-finder-filter-brand-button"
          :outline="brand.slug === selectedBrand"
          tag="button"
          @click="selectBrand(brand.slug)"
        >
          <CdnImage
            :src="brand.logo"
            :alt="brand.name"
            lazy
            render-without-figure
            :style="`aspect-ratio: ${brand.aspectRatio}`"
          />
        </LinkButton>
      </div>

      <div
        v-if="compatibleModels"
        class="panzerglass__product-finder-filter"
      >
        <LinkButton
          v-for="model in compatibleModels"
          :key="model.productFamily.slug"
          class="panzerglass__product-finder-filter-model-button"
          :outline="model.productFamily.slug === selectedModel"
          tag="button"
          @click="selectedModel = model.productFamily.slug"
        >
          {{ model.productFamily.model }}
        </LinkButton>
      </div>
      <OfferTilesContainer
        v-if="brandOffers"
        :offers="brandOffers"
        tag-title="h3"
      >
      </OfferTilesContainer>
    </GridContainer>

    <section class="panzerglass__section panzerglass__products">
      <ProductOfferTileSMContainer
        :items="getOffersByGroup(promotionsWithOffers, 'second')"
        tile-type="small"
        heading-tag="h3"
      />
    </section>

    <ImageTeaser
      title="Alles für den besten Displayschutz"
      animate-tile
      position-y-desktop="center"
      lazy
      heading-tag="h2"
      style="--container-column-image: 0"
      class="panzerglass__section panzerglass__teaser"
      :background="{
        src: '/content/content-images/landingpages/panzerglass/23af49592b0152813b256f00cf7a8731/teaser-weitere-artikel-s@2x.jpg',
        srcSm:
          '/content/content-images/landingpages/panzerglass/d69917d819645ca2b1e9041e32e8cb5f/teaser-weitere-artikel-m@2x.jpg',
        srcMd:
          '/content/content-images/landingpages/panzerglass/608afb5df56e156b579f245e1a12e95e/teaser-weitere-artikel-l@2x.jpg',
        sizes: 'xs:100vw sm:100vw lg:100vw',
      }"
    >
      <template #button>
        <LinkButton
          :primary="true"
          tag="nuxt-link"
          tealium-name="zur-panzerglass"
          to="/zubehoer/_panzerglass"
        >
          Alle PanzerGlass Produkte
        </LinkButton>
      </template>
    </ImageTeaser>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useLazyAsyncData } from '#imports';
import {
  GridContainer,
  Heading,
  Paragraph,
  ImageTextGridRow,
  ImageTeaser,
  LinkButton,
  slugify,
  VideoPlayer,
} from '@i22-td-smarthome/component-library';

import {
  getOffersByGroup,
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '@/lib/offers-by-group';
import useScrollAnimation from '@/components/anker/animations/scroll-animation';
import Breadcrumb from '@/components/breadcrumb.vue';
import CdnImage from '@/components/cdn-image.vue';
import ProductOfferTileSMContainer from '@/components/product-offer-tile-s-m-container.vue';

import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import { addLogo, type BrandWithLogo } from '~/lib/brand';
import ProductOfferTileXlContainer from '~/components/product-offer-tile-xl-container.vue';
import offers, { type ListOffer } from '~/lib/goliath/offers';
import type {
  OfferCompatibleProductFamilyAggregation,
  OfferFilterParams,
} from '~/lib/goliath/goliath.types';
import OfferTilesContainer from '~/components/offer-tiles-container.vue';

const TEASER_DATA: Promotion<any>[] = [
  {
    articleNumber: '99935822',
    tileGroups: ['highlight'],
    title: 'Display- Schutz',
    description: 'Apple iPhone 16 / 15 / 15 Pro',
    ambientImage: {
      src: '/content/content-images/landingpages/panzerglass/dea080b12843344c7d87f22b81d04f3e/panzerglass-hero-teaser-bg@2x.jpg',
      alt: 'Zwei personen vor einem modernen Gebäude mit einem Handy in der Hand',
    },
    ambientImageSizes: 'xs:100vw md:100vw lg:80vw',
    productImage: {
      posY: {
        xs: '10%',
      },
    },
  },
  {
    articleNumber: '5715685002311',
    tileGroups: ['second'],
  },
  {
    articleNumber: '99935826',
    tileGroups: ['second'],
  },
  {
    articleNumber: '99935822',
    tileGroups: ['second'],
  },
];

const textImageRows = [
  // {
  //   title: 'Hoops® für Deine Kamera',
  //   contentList: [
  //     'Maximaler Schutz für deine Kameralinsen',
  //     'Hoops® Kamerschutz erhältlich in Transparent, Ceramic und Schwarz',
  //     'Keine Beeinträchtigung der Fotoqualität',
  //   ],
  //   src: '/content/content-images/landingpages/panzerglass/e8f5bb8491e64d273ac3b5f33d4a9024/kategorie-teaser-hoops@2x.jpg',
  //   alt: 'Die Rückseite eines Handys mit Schutzlinsen auf der Kamera',
  //   style: 'aspect-ratio: 131 / 52',
  //   align: 'left',
  //   linkText: 'Zu den Hoops',
  //   href: 'displex-fullcover',
  // },
  {
    title: 'Privacy Glass',
    contentList: [
      'Spezieller Sichtschutzfilter für deine Privatsphäre',
      'Reduziert Fingerabdrücke und Fettspuren, EasyAligner für kinderleichte Installation.',
      'Dreifach höhere Kratzfestigkeit, Schutz vor Stößen, Stürzen und Alltagsgefahren.',
    ],
    src: '/content/content-images/landingpages/panzerglass/b627833d97f557c8f220d4cc04bc5dba/kategorie-teaser-privacy@2x.jpg',
    alt: 'Eine getönte Schutzfolie vor einem Handydisplay.',
    style: 'aspect-ratio: 131 / 52',
    align: 'right',
    linkText: 'Zum Privacy Glass Displayschutz ',
    href: '/zubehoer/schutzfolien-schutzglaeser/_panzerglass?filters=Privacy',
  },
  {
    title: 'Ceramic Glass',
    contentList: [
      '5-mal stärker als unser bisher stärkster Schutz mit Beschichtung gegen Fingerabdrücke und Fettflecken',
      'Zertifizierte japanische Glaskeramik von Ohara für maximale Festigkeit und kristallklare Transparenz.',
      'EasyAligner für kinderleichte Montage',
    ],
    src: '/content/content-images/landingpages/panzerglass/f8ac9fd28fb32d9f89bdd6ddd451803b/kategorie-teaser-ceramic-glass@2x.jpg',
    alt: 'Eine Nahaufnahme von einem Handybildschirm mit einer Schutzfolie davor.',
    style: 'aspect-ratio: 131 / 52',
    align: 'left',
    linkText: 'Zum Ceramic Glass Displayschutz',
    href: '/zubehoer/schutzfolien-schutzglaeser/_panzerglass?filters=Ceramic',
  },
];

Track({ page_type: 'theme.landing' });
getHeader({
  title: ref('PanzerGlass Displayschutz'),
  isIndexed: ref(false),
  description: ref('Displayschutz für Smartphones, Tablets und Uhren!'),
});

const { data: promotionsWithOffers, error: errorData } = useLazyAsyncData(
  'panzerglass',
  (): Promise<PromotionOffer[]> => loadOffersForPromotions(TEASER_DATA, false, true, false)
);

// handle errors
if (errorData.value) {
  console.error(errorData.value);
  // Show error page
  // throw useNuxtApp().nuxt2Context.error(errorData.value);
}

const brands: BrandWithLogo[] = [
  addLogo({ name: 'Apple', slug: 'apple' }),
  addLogo({ name: 'Samsung', slug: 'samsung' }),
];

/*
const campatibleFamilies = computed(() => {
  const compatibility = listOffers.value?.aggregations?.compatibleProductFamilies;
  if (!compatibility) return [];
  const grouped = compatibility.map((entry) => ({
    groupName: entry.productFamily?.brand?.name,
    label: entry.model,
    value: entry.slug,
    count: entry.offersCount || 0,
  }));
  console.log(grouped);
  console.log(filteredOptions('', grouped, true));
  return filteredOptions('', grouped, true);
});
*/
const selectedBrand = ref<string | undefined>(undefined);
const selectedModel = ref<string | undefined>(undefined);

const brandOffers = ref<ListOffer[] | undefined>(undefined);

const compatibleModels = ref<OfferCompatibleProductFamilyAggregation[] | undefined>(undefined);

const selectBrand = (slug) => {
  selectedBrand.value = slug;
  selectedModel.value = undefined;
};

watch(
  [selectedBrand, selectedModel],
  async () => {
    if (!selectedBrand.value && !selectedModel.value) {
      selectBrand(undefined);
      return;
    }
    try {
      const filter: OfferFilterParams = {
        productAvailable: true,
        listed: true,
        compatibleBrandSlugs: [selectedBrand.value || ''],
        productCategoryPaths: ['zubehoer/schutzfolien-schutzglaeser'],
        brandSlugs: ['panzerglass'],
      };
      if (selectedModel.value) {
        filter.compatibleProductFamilySlugs = [selectedModel.value];
      }
      const result = await offers(
        {
          filter,
        },
        true
      );
      brandOffers.value = result?.entries;
      compatibleModels.value = result?.aggregations?.compatibleProductFamilies;
    } catch (e) {
      console.error(e);
    }
  },
  {
    immediate: true,
  }
);

const breadcrumb = [
  {
    label: 'PangerGlass',
    to: 'panzerglass',
  },
];

const { createScrollTrigger, createTweenTo } = useScrollAnimation();

onMounted(() => {
  [
    '.panzerglass__features-item-top-left',
    '.panzerglass__features-item-top-right',
    '.panzerglass__features-item-bottom-left',
    '.panzerglass__features-item-bottom-right',
  ].forEach((el) => {
    createScrollTrigger({
      trigger: el,
      start: 'top center',
      end: 'top-=50px center',
      scrub: 1,
      animation: createTweenTo(el, {
        y: '0%',
        opacity: 1,
      }),
    });
  });
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.panzerglass {
  &__section {
    margin-bottom: $grid-base * 6;

    @include mq($mq-sm) {
      margin-bottom: $grid-base * 8;
    }
  }

  &__stage {
    position: relative;

    &-background {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
      background: linear-gradient(146deg, #80b9cc 16.58%, #459ab7 79.91%);
      opacity: 0.3;
    }

    &-container {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      grid-template-areas: 'header' 'copy' 'image';
      padding-inline: $grid-base;
      grid-gap: $grid-base * 2;

      @include mq(false, $mq-sm - 1) {
        text-align: center;
      }

      @include mq($mq-sm) {
        grid-template-areas: '. image' 'header image' 'copy image' ' . image';
        grid-template-rows: auto min-content min-content auto;
      }

      @include mq($mq-md) {
        grid-template-columns: 35% 1fr;
      }
    }

    &-header {
      grid-area: header;
      @include mq(false, $mq-sm - 1) {
        padding-top: $grid-base * 2;
      }

      &-logo {
        display: block;
        width: 290px;

        @include mq(false, $mq-sm - 1) {
          margin-inline: auto;
        }
      }

      &-brand {
        background: url('/content/content-images/brand-logos/004117f29eeff4a861a7661f247572d2/panzerglass.svg')
          no-repeat;
        background-size: contain;
        display: inline-block;
        width: 290px;
        height: 34px;
        text-indent: -9999px;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    &-copy {
      grid-area: copy;
      margin-bottom: 0;

      @include mq($mq-md) {
        font-size: 24px;
        line-height: 29px;
      }
    }

    &-image {
      grid-area: image;
      margin-bottom: $grid-base * -2;

      @include mq($mq-md) {
        margin-bottom: -50px;
        text-align: center;
      }

      :deep(img) {
        border-radius: $grid-base * 2;
        aspect-ratio: 1;
        height: 300px;
        object-fit: contain;

        @include mq($mq-sm) {
          height: 50vw;
          max-height: 550px;
        }

        @include mq($mq-md) {
          height: 742px;
        }
      }
    }
  }

  &__features {
    text-align: center;

    &-container {
      position: relative;
      max-width: 1280px;
      margin: 0 auto;
    }

    &-title {
      text-align: center;
      margin-bottom: $grid-base * 4;
      @include mq($mq-md) {
        margin-bottom: $grid-base * 6;
      }
    }

    &-img {
      width: 100%;
      max-width: 280px;
      @include mq(false, $mq-sm - 1) {
        margin-top: 150px;
        margin-bottom: 150px;
      }

      @include mq($mq-sm, $mq-md - 1) {
        margin-top: 100px;
        margin-bottom: 100px;
      }

      @include mq($mq-sm) {
        width: 400px;
        max-width: none;
      }
    }

    &-item {
      font-weight: 800;
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transform: translate(0px, -20%);

      &-icon {
        width: 75px;

        @include mq($mq-md) {
          width: 100px;
        }
      }

      &-top-left {
        left: 5%;
        top: $grid-base;

        @include mq($mq-md) {
          left: 10%;
        }
      }

      &-top-right {
        right: 5%;
        top: $grid-base + 75px;
        @include mq($mq-md) {
          right: 10%;
        }
      }

      &-bottom-left {
        bottom: $grid-base + 75px;
        left: 5%;

        @include mq($mq-md) {
          left: 10%;
        }
      }

      &-bottom-right {
        right: 5%;
        bottom: $grid-base;

        @include mq($mq-md) {
          right: 10%;
        }
      }
    }
  }

  &__video {
    background: black;

    :deep(video) {
      aspect-ratio: 854 / 480;
    }

    &-player {
      line-height: 0;
    }
  }

  &__image-text-rows {
    &-entry {
      margin-bottom: $grid-base * 4;

      @include mq($mq-sm) {
        margin-bottom: $grid-base * 6;
      }
    }

    &-list {
      margin-bottom: $grid-base;
    }
  }

  &__sustainability {
    position: relative;
    max-width: $grid-max;
    margin-inline: auto;
    padding-bottom: 120px;
    display: flex;
    justify-content: center;
    padding-inline: 7vw;

    @include mq($mq-sm) {
      justify-content: flex-end;
    }

    @include mq($mq-md) {
      padding-inline: 160px;
    }

    &-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 300px;
      z-index: -1;

      :deep(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center top;
      }
    }

    &-container {
      padding-top: $grid-base * 2;
    }

    &-content {
      background: #fff;
      color: #000;
      border-radius: $grid-base * 2;
      padding: $grid-base * 2;
      max-width: 700px;

      @include mq($mq-sm) {
        padding: $grid-base * 4;
      }
    }

    &-claim {
      aspect-ratio: 640 / 589;
      max-width: 70vw;
      max-height: 323px;
      display: block;
      margin-inline: auto;
      margin-bottom: $grid-base;

      @include mq($mq-sm) {
        margin-right: 0;
        max-width: none;
        height: 323px;
      }
    }
  }

  &__products {
    margin-top: $grid-base * 2;
    &-title {
      margin-bottom: $grid-base * 4;
    }
  }

  &__product-finder {
    &-title {
      margin-bottom: $grid-base * 4;
    }

    &-filter {
      display: flex;
      grid-gap: $grid-base;
      flex-wrap: wrap;
      margin-bottom: $grid-base * 2;
      margin-inline: $grid-base;
    }

    &-filter-brand-button {
      flex: 1 1 auto;
      img {
        height: 20px;
      }
    }

    &-filter-model-button {
      flex: 0 1 auto;
    }
  }

  &__teaser {
    margin-inline: auto;
    height: 520px;
    max-width: $grid-max;

    @include mq(false, $mq-sm - 1) {
      text-align: center;
      justify-content: stretch;
    }

    :deep(.image-teaser__container) {
      @include mq(false, $mq-sm - 1) {
        margin-top: 36px;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
    }

    :deep(.image-teaser__background-image img) {
      aspect-ratio: 320 / 259;

      @include mq($mq-sm) {
        aspect-ratio: 1024 / 520;
      }

      @include mq($mq-md) {
        aspect-ratio: 1464 / 538;
      }
    }
  }
}
</style>
